/**
 * Hardcoded admin authentication (no backend / no database).
 * Auth state is kept in localStorage. Change the credentials below as needed.
 *
 * Two safeguards on top of the plain flag:
 *  1. Sessions expire 12 hours after login — see SESSION_MS.
 *  2. A session records a fingerprint of the credentials it was created with.
 *     Editing ADMIN_EMAIL / ADMIN_PASSWORD below changes that fingerprint, so
 *     anyone already logged in is signed out the next time the app checks
 *     (on navigation, the periodic check in AdminLayout, or a reload).
 */
export const ADMIN_EMAIL = "bitsclan.admin@bitsclan.com";
export const ADMIN_PASSWORD = "QWERTY@7630";

const AUTH_KEY = "bitsclan_admin_auth";

/** How long a login stays valid without manual logout. */
export const SESSION_MS = 12 * 60 * 60 * 1000; // 12 hours

/**
 * Non-cryptographic fingerprint of the current credentials (djb2). Not for
 * security — only to detect that the credentials changed since a session was
 * created, so we don't store the password itself in localStorage.
 */
const credFingerprint = () => {
  const s = `${ADMIN_EMAIL}::${ADMIN_PASSWORD}`;
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return String(h >>> 0);
};

/** Read + parse the stored session, tolerating the legacy "true" value. */
const readSession = () => {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw || raw === "true") return null; // legacy flag -> force re-login
    const s = JSON.parse(raw);
    return s && typeof s.ts === "number" ? s : null;
  } catch (e) {
    return null;
  }
};

export const login = (email, password) => {
  const ok =
    String(email || "").trim().toLowerCase() === ADMIN_EMAIL &&
    String(password || "") === ADMIN_PASSWORD;
  if (ok) {
    localStorage.setItem(
      AUTH_KEY,
      JSON.stringify({ ts: Date.now(), cred: credFingerprint() })
    );
  }
  return ok;
};

export const logout = () => localStorage.removeItem(AUTH_KEY);

/**
 * True only for a session that is (a) present, (b) younger than SESSION_MS, and
 * (c) created with the current credentials. Any failure clears the session.
 */
export const isAuthenticated = () => {
  const s = readSession();
  if (!s) return false;
  if (Date.now() - s.ts > SESSION_MS) {
    logout();
    return false;
  }
  if (s.cred !== credFingerprint()) {
    logout();
    return false;
  }
  return true;
};

/**
 * When the current session expires (ms since epoch), or null if there is no
 * valid session. Lets the UI schedule an exact auto-logout.
 */
export const sessionExpiresAt = () => {
  const s = readSession();
  if (!s || s.cred !== credFingerprint()) return null;
  return s.ts + SESSION_MS;
};
